import React, { useState } from 'react';
import { XIcon, UserPlusIcon, UsersIcon, AlertCircleIcon, InfoIcon } from 'lucide-react';
import { usePreventScroll } from '../../hooks/usePreventScroll';
import { SECURITY_CONFIG } from '../../config/security';
import { FormField } from '../common/FormField';
import { toast } from 'sonner';
import { validateEmail } from '../../utils/validation';
interface Props {
  isOpen: boolean;
  onClose: () => void;
}
interface InviteFormData {
  organizationName: string;
  adminEmail: string;
  maxSpaces: number;
  maxChatbots: number;
  maxAdminUsers: number;
  maxMemberUsers: number;
  customMessage?: string;
}
export const InviteClientModal = ({
  isOpen,
  onClose
}: Props) => {
  const [formData, setFormData] = useState<InviteFormData>({
    organizationName: '',
    adminEmail: '',
    maxSpaces: SECURITY_CONFIG.organization.defaults.knowledgeSpaces,
    maxChatbots: SECURITY_CONFIG.organization.defaults.chatbots,
    maxAdminUsers: SECURITY_CONFIG.organization.defaults.adminUsers,
    maxMemberUsers: SECURITY_CONFIG.organization.defaults.memberUsers
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Add usePreventScroll hook
  usePreventScroll(isOpen);
  const validateForm = (): boolean => {
    if (!formData.organizationName.trim()) {
      setError('Organization name is required');
      return false;
    }
    if (!formData.adminEmail.trim() || !validateEmail(formData.adminEmail)) {
      setError('Please enter a valid email address');
      return false;
    }
    const adminEmailDomain = formData.adminEmail.split('@')[1];
    if (adminEmailDomain === 'sprinklenetlabs.com') {
      setError('Admin email cannot be an internal Sprinklenet email');
      return false;
    }
    return true;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validateForm()) {
      return;
    }
    try {
      setLoading(true);
      // Handle invitation logic here
      console.log('Inviting client:', formData);
      toast.success('Invitation sent successfully');
      onClose();
    } catch (err) {
      setError('Failed to send invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-179">
      <div className="bg-surface-light border border-surface-border rounded-none w-full max-w-2xl max-h-[90vh] flex flex-col" data-id="element-180">
        <div className="px-6 py-4 border-b border-surface-border flex items-center justify-between shrink-0" data-id="element-181">
          <div className="flex items-center gap-3" data-id="element-182">
            <UserPlusIcon size={20} className="text-accent-light" data-id="element-183" />
            <h2 className="text-lg font-medium text-white font-mono" data-id="element-184">
              Invite New Client
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-300 transition-colors" data-id="element-185">
            <XIcon size={20} data-id="element-186" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto" data-id="element-187">
          <div className="p-6 space-y-6" data-id="element-188">
            <FormField label="Organization Name" type="text" value={formData.organizationName} onChange={value => setFormData(prev => ({
            ...prev,
            organizationName: value
          }))} required placeholder="Enter organization name" tooltip="The name of the client organization" data-id="element-189" />
            <FormField label="Admin Email" type="email" value={formData.adminEmail} onChange={value => setFormData(prev => ({
            ...prev,
            adminEmail: value
          }))} required placeholder="Enter admin email address" tooltip="The email address of the organization's admin" data-id="element-190" />
            <div className="bg-surface p-4 border border-surface-border rounded-none space-y-6" data-id="element-191">
              <div className="flex items-center gap-2" data-id="element-192">
                <h3 className="text-sm font-medium text-white font-mono" data-id="element-193">
                  Resource Limits
                </h3>
                <div className="relative group" data-id="element-194">
                  <InfoIcon size={14} className="text-gray-400 cursor-help" data-id="element-195" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-surface-light border border-surface-border rounded-none text-xs text-gray-300 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity" data-id="element-196">
                    Set the maximum resources available to this organization
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-197">
                <FormField label="Max Knowledge Spaces" type="number" value={formData.maxSpaces.toString()} onChange={value => setFormData(prev => ({
                ...prev,
                maxSpaces: parseInt(value) || 0
              }))} required min={1} max={SECURITY_CONFIG.organization.maxLimits.knowledgeSpaces} tooltip="Maximum number of knowledge spaces allowed" data-id="element-198" />
                <FormField label="Max Total Chatbots" type="number" value={formData.maxChatbots.toString()} onChange={value => setFormData(prev => ({
                ...prev,
                maxChatbots: parseInt(value) || 0
              }))} required min={1} max={SECURITY_CONFIG.organization.maxLimits.chatbots} tooltip="Maximum number of total chatbots allowed for the organization" data-id="element-199" />
                <div className="flex items-start gap-3" data-id="element-200">
                  <div className="p-2 bg-surface-light border border-surface-border rounded-none" data-id="element-201">
                    <UsersIcon size={18} className="text-accent-light" data-id="element-202" />
                  </div>
                  <div className="flex-1 space-y-4" data-id="element-203">
                    <FormField label="Max Admin Users" type="number" value={formData.maxAdminUsers.toString()} onChange={value => setFormData(prev => ({
                    ...prev,
                    maxAdminUsers: parseInt(value) || 0
                  }))} required min={1} max={SECURITY_CONFIG.organization.maxLimits.adminUsers} tooltip="Maximum number of admin users allowed" data-id="element-204" />
                    <FormField label="Max Member Users" type="number" value={formData.maxMemberUsers.toString()} onChange={value => setFormData(prev => ({
                    ...prev,
                    maxMemberUsers: parseInt(value) || 0
                  }))} required min={1} max={SECURITY_CONFIG.organization.maxLimits.memberUsers} tooltip="Maximum number of member users allowed" data-id="element-205" />
                  </div>
                </div>
              </div>
            </div>
            <FormField label="Custom Message (Optional)" type="textarea" value={formData.customMessage || ''} onChange={value => setFormData(prev => ({
            ...prev,
            customMessage: value
          }))} placeholder="Add a personal message to the invitation email..." tooltip="Optional message to include in the invitation email" data-id="element-206" />
            {error && <div className="flex items-start gap-2 p-3 bg-status-error/10 border border-status-error/30 rounded-none" data-id="element-207">
                <AlertCircleIcon size={18} className="text-status-error shrink-0 mt-0.5" data-id="element-208" />
                <p className="text-sm text-status-error" data-id="element-209">{error}</p>
              </div>}
          </div>
        </form>
        <div className="px-6 py-4 border-t border-surface-border flex justify-end gap-3 shrink-0" data-id="element-210">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-gray-300 transition-colors" disabled={loading} data-id="element-211">
            Cancel
          </button>
          <button type="submit" disabled={loading} className="px-6 py-2 bg-accent hover:bg-accent-light text-white text-sm 
                     font-mono uppercase tracking-wide rounded-none transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-id="element-212">
            {loading ? <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-213" />
                <span data-id="element-214">Sending...</span>
              </> : <>
                <UserPlusIcon size={16} data-id="element-215" />
                <span data-id="element-216">Send Invitation</span>
              </>}
          </button>
        </div>
      </div>
    </div>;
};